// Product reviews pop-up: the first page of reviews is server-rendered from Judge.me's saved widget.
// This loads the remaining pages from Judge.me's public review endpoint as the shopper scrolls,
// and fills in review dates (normally written by Judge.me's own script).
const root = document.querySelector('[data-product-reviews]');

const formatDates = (scope) => {
  const formatter = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  scope.querySelectorAll('time.jdgm-rev__timestamp[datetime]').forEach((time) => {
    if (time.textContent.trim()) return;
    const date = new Date(time.getAttribute('datetime'));
    if (!Number.isNaN(date.getTime())) time.textContent = formatter.format(date);
  });
};


// Review photos: Judge.me lazy-loads thumbnails with its own script, so set them here,
// label each one clearly, and open the full-size photo in a viewer inside the pop-up.
const preparePhotos = (scope, rootEl) => {
  const label = rootEl.dataset.viewPhotoLabel || 'View photo';
  scope.querySelectorAll('.jdgm-rev__pic-link').forEach((link) => {
    if (link.dataset.prepared) return;
    link.dataset.prepared = 'true';
    link.classList.remove('jdgm--loading');
    const img = link.querySelector('img[data-src]');
    if (img && !img.getAttribute('src')) {
      img.src = img.dataset.src;
      img.loading = 'lazy';
    }
    const caption = document.createElement('span');
    caption.className = 'review-photo-caption';
    caption.textContent = label;
    link.append(caption);
  });
};

const setupPhotoViewer = (rootEl) => {
  const viewer = rootEl.closest('dialog')?.querySelector('[data-photo-viewer]');
  if (!viewer) return;
  const viewerImg = viewer.querySelector('img');
  const close = () => { viewer.hidden = true; viewerImg.removeAttribute('src'); };
  rootEl.addEventListener('click', (event) => {
    const link = event.target.closest('.jdgm-rev__pic-link');
    if (!link) return;
    event.preventDefault();
    viewerImg.src = link.dataset.mfpSrc || link.href;
    viewerImg.alt = link.getAttribute('aria-label') || '';
    viewer.hidden = false;
    viewer.querySelector('[data-photo-close]')?.focus();
  });
  viewer.addEventListener('click', (event) => {
    if (event.target === viewer || event.target.closest('[data-photo-close]')) close();
  });
  viewer.closest('dialog')?.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !viewer.hidden) { event.preventDefault(); event.stopPropagation(); close(); }
  }, true);
  viewer.closest('dialog')?.addEventListener('close', close);
};

if (root) {
  preparePhotos(root, root);
  setupPhotoViewer(root);
  formatDates(root);

  const list = root.querySelector('.jdgm-rev-widg__reviews');
  const more = root.querySelector('[data-reviews-more]');
  const sentinel = root.querySelector('[data-reviews-sentinel]');
  let totalPages = Number(root.dataset.totalPages || 1);
  let page = 1;
  let loading = false;

  const finish = () => {
    if (more) more.hidden = true;
    observer?.disconnect();
  };

  const fetchPage = async (pageNumber) => {
    const params = new URLSearchParams({
      url: root.dataset.shopDomain,
      shop_domain: root.dataset.shopDomain,
      platform: 'shopify',
      page: String(pageNumber),
      per_page: root.dataset.perPage || '5',
      product_id: root.dataset.productId,
    });
    const response = await fetch(`https://judge.me/reviews/reviews_for_widget?${params}`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(String(response.status));
    return response.json();
  };

  // The saved copy of the first page can lag behind Judge.me, so swap in the live
  // first page (and the live review count) as soon as the page loads.
  const refreshFirstPage = async () => {
    if (!list) return;
    try {
      const data = await fetchPage(1);
      const template = document.createElement('template');
      template.innerHTML = data.html || '';
      const reviews = template.content.querySelectorAll('.jdgm-rev');
      if (!reviews.length) return;
      list.querySelectorAll('.jdgm-rev').forEach((review) => review.remove());
      reviews.forEach((review) => list.append(review));
      formatDates(list);
      preparePhotos(list, root);
      const total = Number(data.total_count);
      if (total > 0) {
        totalPages = Math.ceil(total / Number(root.dataset.perPage || 5));
        const countText = document.querySelector('[data-reviews-count]');
        if (countText?.dataset.countTemplate) countText.textContent = countText.dataset.countTemplate.replace('__COUNT__', String(total));
        if (totalPages > 1 && more) more.hidden = false;
      }
    } catch (error) {
      // Keep the saved reviews if the live feed can't be reached.
    }
  };

  const loadNext = async () => {
    if (loading || !list || page >= totalPages) return finish();
    loading = true;
    more?.setAttribute('aria-busy', 'true');
    const next = page + 1;
    try {
      const data = await fetchPage(next);
      const template = document.createElement('template');
      template.innerHTML = data.html || '';
      const reviews = template.content.querySelectorAll('.jdgm-rev');
      reviews.forEach((review) => list.append(review));
      formatDates(list);
      preparePhotos(list, root);
      page = next;
      if (!reviews.length || page >= totalPages) finish();
    } catch (error) {
      finish();
    } finally {
      loading = false;
      more?.removeAttribute('aria-busy');
    }
  };

  const scroller = root.closest('[data-reviews-scroll]') || root;
  const observer = 'IntersectionObserver' in window && sentinel
    ? new IntersectionObserver((entries) => { if (entries[0].isIntersecting) loadNext(); }, { root: scroller, rootMargin: '200px' })
    : null;

  await refreshFirstPage();

  if (totalPages <= 1) {
    finish();
  } else {
    if (sentinel && observer) observer.observe(sentinel);
    more?.addEventListener('click', loadNext);
  }
}
